// src/modules/student-exit/components/GuardianStudentExitForm.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, message, TimePicker } from "antd";
import { useTranslation } from "react-i18next";
import CustomModal from "components/Common/CustomModal";
import dayjs from "dayjs";
import CommonSelectLite from "components/Common/CommonSelectLite";
import { getDisabledHoursForExitType, getDisabledMinutesForExitType } from "utils/schoolUtils";
import { LocalizedTimePicker } from "components/Common/ui/LocalizedDatePicker";
import { toTodayDateWithTime } from "utils/dateUtils";

const { TextArea } = Input;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<boolean>;
  studentId: string;
  exitType: "morning" | "afternoon" | "early";
  exitTime: string;
  onGetGuardianByStudentId: (studentId: string) => Promise<any>;
  onGetAuthorizedByStudentId: (studentId: string) => Promise<any>;
};

const GuardianStudentExitForm: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  studentId,
  exitType,
  exitTime,
  onGetGuardianByStudentId,
  onGetAuthorizedByStudentId,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [pickupType, setPickupType] = useState<"guardian" | "authorized">(
    "guardian"
  );
  const [loadingGuardians, setLoadingGuardians] = useState(false);
  const [loadingAuthorized, setLoadingAuthorized] = useState(false);
  const [guardians, setGuardians] = useState<any[]>([]);
  const [authorizedPersons, setAuthorizedPersons] = useState<any[]>([]);

  const exitTypeOptions = [
    { value: "morning", label: t("studentExit.exitType.morning") },
    { value: "afternoon", label: t("studentExit.exitType.afternoon") },
    { value: "early", label: t("studentExit.exitType.early") },
  ];




  useEffect(() => {
    if (isOpen && studentId) {
      const defaultValues = {
        studentId: studentId,
        exitType: exitType,
        exitTime: dayjs(toTodayDateWithTime(exitTime)) ,
        notes: "",
        authorizedById: null,
        guardianId: null,
      };

      form.setFieldsValue(defaultValues);

      fetchGuardians(studentId);
    }
  }, [isOpen, studentId, exitType, form]);

  useEffect(() => {
    if (isOpen && studentId) {
      if (pickupType === "guardian") {
        fetchGuardians(studentId);
      } else {
        fetchAuthorizedPersons(studentId);
      }
    }
  }, [isOpen, studentId, pickupType]);

  const fetchGuardians = async (studentId: string) => {
    setLoadingGuardians(true);
    try {
      const guardiansData = await onGetGuardianByStudentId(studentId);
      setGuardians(guardiansData || []);

      if (
        guardiansData &&
        guardiansData.length > 0 &&
        !form.getFieldValue("guardianId")
      ) {
        form.setFieldsValue({
          guardianId: guardiansData[0].guardian?.id,
        });
      }
    } catch (error) {
      console.error("Error fetching guardians:", error);
      setGuardians([]);
    } finally {
      setLoadingGuardians(false);
    }
  };

  const fetchAuthorizedPersons = async (studentId: string) => {
    setLoadingAuthorized(true);
    try {
      const authorizedData = await onGetAuthorizedByStudentId(studentId);
      setAuthorizedPersons(authorizedData || []);

      if (
        authorizedData &&
        authorizedData.length > 0 &&
        !form.getFieldValue("authorizedById")
      ) {
        form.setFieldsValue({
          authorizedById: authorizedData[0].id,
        });
      }
    } catch (error) {
      console.error("Error fetching authorized persons:", error);
      setAuthorizedPersons([]);
      message.error(t("studentExit.error.fetchAuthorized"));
    } finally {
      setLoadingAuthorized(false);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (!values.guardianId && !values.authorizedById) {
        message.error(t("studentExit.validation.pickupPersonRequired"));
        return;
      }

      const formattedValues = {
        ...values,
        exitTime: values.exitTime
          ? values.exitTime.format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
      };

      setSaving(true);
      const response = await onSave(formattedValues);
      if (response) {
        onClose();
        form.resetFields();
      }
    } catch (err: any) {
      console.log("Validation error:", err);
    } finally {
      setSaving(false);
    }
  };

  const handlePickupTypeChange = (value: "guardian" | "authorized") => {
    setPickupType(value);
    form.setFieldsValue({
      guardianId: undefined,
      authorizedById: undefined,
    });
  };

  const handleClose = () => {
    form.resetFields();
    setPickupType("guardian");
    setGuardians([]);
    setAuthorizedPersons([]);
    onClose();
  };


  const guardianOptions = useMemo(
    () =>
      guardians.map((guardian: any) => ({
        value: guardian.guardian.id,
        label: `${guardian.guardian.user?.firstName || ""} ${guardian.guardian.user?.lastName || ""
          }`.trim(),
      })),
    [guardians]
  );

  const authorizedOptions = useMemo(
    () =>
      authorizedPersons.map((person: any) => ({
        value: person.id,
        label: `${person.fullName || ""} (${t(
          `authorizedPickUpPerson.relationship.${person.relationship}`
        )}) ${person.phone ? `- ${person.phone}` : ""}`.trim(),
      })),
    [authorizedPersons, t]
  );

  return (
    <CustomModal
      title={t("studentExit.add")}
      isOpen={isOpen}
      onClose={handleClose}
      onSave={handleOk}
      saveText={t("save")}
      loading={saving}
    >
      <Form form={form} layout="vertical" preserve={true}>
        <Form.Item name="studentId" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item name="exitType" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item label={t("studentExit.form.exitType")}>
          <Input value={t(`studentExit.exitType.${exitType}`)} disabled />
        </Form.Item>

        <Form.Item label={t("studentExit.form.pickupType")}>
          <CommonSelectLite
            placeholder={t("studentExit.form.pickupType")}
            onChange={handlePickupTypeChange}
            options={[
              { value: "guardian", label: t("studentExit.pickupType.guardian") },
              {
                value: "authorized",
                label: t("studentExit.pickupType.authorized"),
              },
            ]}
            optionValueKey="value"
            optionLabelKey="label"
            value={pickupType}
          />
        </Form.Item>

        {pickupType === "guardian" && (
          <Form.Item
            label={t("studentExit.form.guardian")}
            name="guardianId"
            rules={[
              {
                required: pickupType === "guardian",
                message: t("studentExit.validation.guardianRequired"),
              },
            ]}
          >
            <CommonSelectLite
              placeholder={
                loadingGuardians
                  ? t("studentExit.loadingGuardians")
                  : t("studentExit.placeholder.selectGuardian")
              }
              options={guardianOptions}
              optionValueKey="value"
              optionLabelKey="label"
            />
          </Form.Item>
        )}

        {pickupType === "authorized" && (
          <Form.Item
            label={t("studentExit.form.authorizedBy")}
            name="authorizedById"
            rules={[
              {
                required: pickupType === "authorized",
                message: t("studentExit.validation.authorizedRequired"),
              },
            ]}
          >
            <CommonSelectLite
              placeholder={
                loadingAuthorized
                  ? t("studentExit.loadingAuthorized")
                  : t("studentExit.placeholder.selectAuthorizedBy")
              }
              options={authorizedOptions}
              optionValueKey="value"
              optionLabelKey="label"
            />
          </Form.Item>
        )}

        <Form.Item
          label={t("studentExit.form.exitTime")}
          name="exitTime"
          rules={[
            {
              required: true,
              message: t("studentExit.validation.exitTimeRequired"),
            },
          ]}
        >


          <LocalizedTimePicker
            format="HH:mm"
            minuteStep={5}
            style={{ width: "100%" }}
            placeholder={t("studentExit.placeholder.exitTime")}
            disabled={exitType === "morning" || exitType === "afternoon"}
            disabledTime={() => ({
              disabledHours: () => { return getDisabledHoursForExitType(exitType, true); },
              disabledMinutes: (selectedHour) => { return getDisabledMinutesForExitType(exitType, selectedHour) }
            })}
          />
        </Form.Item>

        <Form.Item label={t("studentExit.form.notes")} name="notes">
          <TextArea
            rows={3}
            placeholder={t("studentExit.placeholder.notes")}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default GuardianStudentExitForm;
